import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import * as d3 from "d3";
import { CrudService } from "../../../Services/crud.service";

interface FlowLink { from: string; to: string; count: number; }

export interface RenderNode {
  id: string;
  name: string;
  col: number;
  x: number;
  y: number;
  count: number;
  color: string;
}

@Component({
  selector: "app-nav-flow",
  templateUrl: "./nav-flow.component.html",
  standalone: false,
  styles: [`
    :host { display: block; }
    .pf-wrap {
      width: 100%;
      background: #f8faff;
      border-top: 1px solid #e8edf5;
      overflow-x: auto;
      overflow-y: visible;
      padding: 16px 0 32px;
      min-height: 120px;
    }
    .pf-state {
      text-align: center;
      padding: 60px;
      color: #94a3b8;
      font-size: 14px;
    }
    .pf-chart {
      position: relative;
    }
    .pf-svg {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      overflow: visible;
      pointer-events: none;
    }
    .pf-col-label {
      position: absolute;
      top: 10px;
      text-align: center;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      color: #475569;
      background: #e2e8f0;
      border-radius: 12px;
      padding: 4px 0;
      z-index: 3;
    }
    .pf-node {
      position: absolute;
      background: #ffffff;
      border-radius: 7px;
      border: 1px solid #d1d9e6;
      border-left: 5px solid #4285f4;
      box-shadow: 0 2px 8px rgba(0,0,0,0.10);
      padding: 10px 12px;
      z-index: 2;
      box-sizing: border-box;
      overflow: hidden;
    }
    .pf-node-name {
      font-size: 12px;
      font-weight: 600;
      color: #334155;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 4px;
    }
    .pf-node-count {
      font-size: 18px;
      font-weight: 700;
      line-height: 1.1;
    }
    .pf-node-visits {
      font-size: 10px;
      font-weight: 500;
      color: #94a3b8;
      margin-left: 3px;
    }
  `],
})
export class NavFlowComponent implements OnChanges, AfterViewInit {
  @Input() filterQuery: string = "";
  @ViewChild("flowContainer") flowContainer!: ElementRef;
  @ViewChild("svgEl") svgEl!: ElementRef;

  private initialized = false;
  private links: FlowLink[] = [];
  private colLinks: FlowLink[][] = [];
  private nodeMap: Record<string, RenderNode> = {};

  loading = false;
  noData = false;

  renderNodes: RenderNode[] = [];
  columnLabels: string[] = [];
  chartWidth  = 700;
  chartHeight = 400;

  readonly cardW = 180;
  readonly cardH = 70;
  private readonly CARD_GAP = 12;
  private readonly COL_GAP  = 140;
  private readonly PAD_L    = 20;
  private readonly PAD_T    = 52;

  private readonly PALETTE = [
    "#4285f4","#34a853","#fbbc05","#ea4335",
    "#9c27b0","#00bcd4","#ff9800","#795548",
    "#607d8b","#e91e63",
  ];
  private colorMap: Record<string, string> = {};

  constructor(
    private crudService: CrudService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit() {
    this.initialized = true;
    if (this.filterQuery !== undefined) setTimeout(() => this.fetchAndRender(), 200);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["filterQuery"] && this.initialized) {
      setTimeout(() => this.fetchAndRender(), 200);
    }
  }

  private color(name: string): string {
    if (!this.colorMap[name]) {
      const idx = Object.keys(this.colorMap).length;
      this.colorMap[name] = this.PALETTE[idx % this.PALETTE.length];
    }
    return this.colorMap[name];
  }

  formatCount(n: number): string {
    if (n >= 1000) return (n / 1000).toFixed(1) + "k";
    return String(n);
  }

  colHeaderX(ci: number): number {
    return this.PAD_L + ci * (this.cardW + this.COL_GAP);
  }

  private fetchAndRender() {
    this.loading   = true;
    this.noData    = false;
    this.renderNodes = [];
    this.colLinks  = [];
    this.colorMap  = {};

    this.crudService.get(
      null,
      (response: any) => {
        this.loading = false;
        if (!response || response === "No Data Found" || !Array.isArray(response) || !response.length) {
          this.noData = true;
          this.cdr.detectChanges();
          return;
        }
        this.links = response as FlowLink[];
        this.buildLayout();
        this.cdr.detectChanges();
        // Draw SVG bands after Angular has rendered the cards
        setTimeout(() => this.drawBands(), 80);
      },
      `usageAnalytics/getPageFlow?${this.filterQuery}`
    );
  }

  private buildLayout() {
    // Pre-assign colors
    [...new Set([...this.links.map(l => l.from), ...this.links.map(l => l.to)])].forEach(n => this.color(n));

    const { columns, colLinks } = this.buildColumns(this.links);
    this.colLinks = colLinks;

    const outTotal: Record<string, number> = {};
    const inTotal:  Record<string, number> = {};
    this.links.forEach(l => {
      outTotal[l.from] = (outTotal[l.from] || 0) + l.count;
      inTotal[l.to]    = (inTotal[l.to]    || 0) + l.count;
    });

    this.nodeMap    = {};
    this.renderNodes = [];
    this.columnLabels = columns.map((_, ci) => ci === 0 ? "STARTING POINT" : `STEP +${ci}`);

    columns.forEach((nodes, ci) => {
      let y = this.PAD_T;
      nodes.forEach(name => {
        const node: RenderNode = {
          id:    `${ci}__${name}`,
          name,
          col:   ci,
          x:     this.PAD_L + ci * (this.cardW + this.COL_GAP),
          y,
          count: ci === 0 ? (outTotal[name] || 0) : (inTotal[name] || 0),
          color: this.color(name),
        };
        this.renderNodes.push(node);
        this.nodeMap[node.id] = node;
        y += this.cardH + this.CARD_GAP;
      });
    });

    const maxColH = Math.max(...columns.map(nodes => this.PAD_T + nodes.length * (this.cardH + this.CARD_GAP)));
    this.chartHeight = maxColH + 48;
    this.chartWidth  = this.PAD_L + columns.length * (this.cardW + this.COL_GAP) + 40;
  }

  private buildColumns(links: FlowLink[]): { columns: string[][], colLinks: FlowLink[][] } {
    const outEdges: Record<string, FlowLink[]> = {};
    const inSet = new Set<string>();
    links.forEach(l => {
      (outEdges[l.from] ??= []).push(l);
      inSet.add(l.to);
    });

    const allFrom = [...new Set(links.map(l => l.from))];
    const outTotal: Record<string, number> = {};
    const inTotal:  Record<string, number> = {};
    links.forEach(l => {
      outTotal[l.from] = (outTotal[l.from] || 0) + l.count;
      inTotal[l.to]    = (inTotal[l.to]    || 0) + l.count;
    });

    let sources = allFrom.filter(n => !inSet.has(n));
    if (!sources.length) {
      sources = [allFrom.sort((a, b) => (outTotal[b] || 0) - (outTotal[a] || 0))[0]];
    }

    const columns: string[][] = [];
    const colLinks: FlowLink[][] = [];
    const visited = new Set<string>();
    let current = [...sources].sort((a, b) => (outTotal[b] || 0) - (outTotal[a] || 0));

    while (current.length && columns.length < 4) {
      columns.push(current);
      current.forEach(n => visited.add(n));

      const nextLinks: FlowLink[] = [];
      const nextSet = new Set<string>();
      current.forEach(n => {
        (outEdges[n] || []).forEach(l => {
          if (!visited.has(l.to)) { nextLinks.push(l); nextSet.add(l.to); }
        });
      });

      if (!nextSet.size) break;
      colLinks.push(nextLinks);
      current = [...nextSet].sort((a, b) => (inTotal[b] || 0) - (inTotal[a] || 0));
    }

    return { columns, colLinks };
  }

  private drawBands() {
    if (!this.svgEl) return;
    const svgEl = this.svgEl.nativeElement as SVGSVGElement;
    const d3Any = d3 as any;
    d3Any.select(svgEl).selectAll("*").remove();

    const defs = d3Any.select(svgEl).append("defs");
    const columns = this.columnLabels.map((_, ci) =>
      this.renderNodes.filter(n => n.col === ci)
    );

    this.colLinks.forEach((links, pairIdx) => {
      const srcCI = pairIdx;
      const tgtCI = pairIdx + 1;

      const srcY: Record<string, number> = {};
      const tgtY: Record<string, number> = {};
      columns[srcCI]?.forEach(n => { srcY[n.name] = n.y + 6; });
      columns[tgtCI]?.forEach(n => { tgtY[n.name] = n.y + 6; });

      const sorted = [...links].sort(
        (a, b) => (this.nodeMap[`${srcCI}__${a.from}`]?.y || 0)
                - (this.nodeMap[`${srcCI}__${b.from}`]?.y || 0)
      );
      const pairTotal = links.reduce((s, l) => s + l.count, 0);

      sorted.forEach(link => {
        const src = this.nodeMap[`${srcCI}__${link.from}`];
        const tgt = this.nodeMap[`${tgtCI}__${link.to}`];
        if (!src || !tgt) return;

        const bandH = Math.max(4, (link.count / pairTotal) * (this.cardH - 12));
        const x0 = src.x + this.cardW;
        const x1 = tgt.x;
        const y0t = srcY[link.from];
        const y1t = tgtY[link.to];
        srcY[link.from] += bandH + 2;
        tgtY[link.to]   += bandH + 2;

        const mx = (x0 + x1) / 2;
        const path =
          `M${x0},${y0t} C${mx},${y0t} ${mx},${y1t} ${x1},${y1t}` +
          ` L${x1},${y1t+bandH} C${mx},${y1t+bandH} ${mx},${y0t+bandH} ${x0},${y0t+bandH} Z`;

        const gid = `pfg_${pairIdx}_${link.from}_${link.to}`.replace(/[^a-zA-Z0-9_]/g, "_");
        const grad = defs.append("linearGradient").attr("id", gid).attr("x1","0%").attr("x2","100%");
        grad.append("stop").attr("offset","0%")
          .attr("stop-color", this.color(link.from)).attr("stop-opacity", 0.35);
        grad.append("stop").attr("offset","100%")
          .attr("stop-color", this.color(link.to)).attr("stop-opacity", 0.55);

        d3Any.select(svgEl).append("path")
          .datum(link)
          .attr("d", path)
          .attr("fill", `url(#${gid})`)
          .attr("stroke", "none")
          .style("cursor", "pointer")
          .on("mouseover", (ev: any) => { d3Any.select(ev.currentTarget).attr("opacity", 0.72); })
          .on("mouseleave", (ev: any) => { d3Any.select(ev.currentTarget).attr("opacity", 1); });
      });
    });
  }
}
